import useLocalState from '@phntms/use-local-state';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { TemplateList } from '../../../compositions/templates/list/template-list';
import { TemplateCreateModal } from '../../../compositions/templates/modals/template-create-modal/template-create-modal';
import { TemplateModal } from '../../../compositions/templates/modals/template-modal/template-modal';
import { useCurrentHealthProvider } from '../../../data/data/health-providers/use-current-health-provider';
import { useTemplateItems } from '../../../data/data/templates/use-template-items';
import {
  TemplateItemDataFragment,
  TemplateStatus,
} from '../../../data/types/api/graphql';
import { Button, ButtonIcon } from '../../../elements/button/button';
import { Content } from '../../../elements/content/content';
import { BaseInputIcon } from '../../../elements/form/input/base-input';
import { InputField } from '../../../elements/form/input/input-field';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import {
  DataTableLocationState,
  DataTablePageSize,
} from '../../../elements/table/data-table/data-table';
import {
  Pagination,
  PaginationData,
} from '../../../elements/table/pagination/pagination';
import { Tabs } from '../../../elements/tabs/tabs';

export function AdminTemplatesPage() {
  const intl = useIntl();

  const { healthProvider } = useCurrentHealthProvider();

  const [status, setStatus] = useState<TemplateStatus | 'ALL'>(
    TemplateStatus.Active
  );

  const [templateModalOpen, setTemplateModalOpen] = useState<
    TemplateItemDataFragment | false
  >(false);

  const [templateCreateModalOpen, setTemplateCreateModalOpen] =
    useState<boolean>(false);

  const location = useLocation();

  const [limit, setLimit] = useLocalState<number>(
    'dtHealthProfessionalsLimit',
    DataTablePageSize
  );

  const [paging, setPaging] = useState<PaginationData>({
    page: (location?.state as DataTableLocationState)?.page ?? 1,
    total: 0,
  });

  const [query, setQuery] = useState<string>(
    (location?.state as DataTableLocationState)?.query ?? ''
  );

  const { data: templateItemsData, loading: templatesItemsoading } =
    useTemplateItems({
      healthProviderId: healthProvider?.id,
      statuses: [status],
      search: query,
      take: limit,
      skip: (paging.page - 1) * limit,
    });

  const templateItems = useMemo(() => {
    const total = templateItemsData?.totalCount ?? 0;

    if (templateItemsData) {
      setPaging(prevState => ({
        ...prevState,
        total,
      }));
    }

    return templateItemsData?.items ?? [];
  }, [templateItemsData]);

  useEffect(() => {
    if (query.length > 0) {
      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    }
  }, [query]);

  const onHandleSearch = useCallback((value: string) => {
    setQuery(value);
  }, []);

  const onHandleSearchDebounce = useMemo(
    () => debounce(onHandleSearch, 250),
    [onHandleSearch]
  );

  const onHandleLimit = useCallback(
    (value: number) => {
      setLimit(value);

      setPaging(prevState => ({
        ...prevState,
        page: 1,
      }));
    },
    [setLimit]
  );

  const onHandlePagination = useCallback((value: number) => {
    setPaging(prevState => ({
      ...prevState,
      page: value,
    }));
  }, []);

  const onHandleStatus = useCallback((value: TemplateStatus | 'ALL') => {
    setStatus(value);
  }, []);

  const onHandleTemplate = useCallback((value: TemplateItemDataFragment) => {
    setTemplateModalOpen(value);
  }, []);

  return (
    <>
      <Content
        toolbar={
          <>
            <Tabs<TemplateStatus | 'ALL'>
              value={status}
              items={[
                {
                  key: TemplateStatus.Active,
                  title: (
                    <CustomFormattedMessage id="admin.templates.active-templates" />
                  ),
                },
                {
                  key: 'ALL',
                  title: (
                    <CustomFormattedMessage id="admin.templates.all-templates" />
                  ),
                },
              ]}
              onChange={onHandleStatus}
            />

            <InputField
              icon={BaseInputIcon.SEARCH}
              type="search"
              name="search"
              placeholder={intl.formatMessage({
                id: 'common.templates-placeholders',
              })}
              onChange={value => onHandleSearchDebounce(value)}
            />

            <Button
              icon={ButtonIcon.LIST_ADD}
              onClick={() => setTemplateCreateModalOpen(true)}
            >
              <CustomFormattedMessage id="admin.button.create-template" />
            </Button>
          </>
        }
      >
        <TemplateList
          loading={templatesItemsoading}
          items={templateItems}
          onClick={onHandleTemplate}
        />

        <Pagination
          limit={limit}
          paging={paging}
          onChangeLimit={onHandleLimit}
          onChangePage={onHandlePagination}
        />
      </Content>

      {templateModalOpen && (
        <TemplateModal
          templateId={templateModalOpen.id}
          onClose={() => {
            setTemplateModalOpen(false);
          }}
        />
      )}

      {templateCreateModalOpen && (
        <TemplateCreateModal
          onClose={() => setTemplateCreateModalOpen(false)}
        />
      )}
    </>
  );
}
